import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { IconButton } from './UI';

const Wrap = styled.div` display: flex; min-height: 100vh; background: var(--gray-50); `;

const Sidebar = styled.aside`
  width: 240px; background: var(--gray-900); color: #fff;
  display: flex; flex-direction: column; position: fixed; top: 0; bottom: 0; left: 0; z-index: 50;
  transform: ${p => p.$open ? 'translateX(0)' : 'translateX(0)'};
  transition: transform .3s;
  @media(max-width:1024px){
    transform: ${p => p.$open ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;
const SidebarOverlay = styled.div`
  display: none;
  @media(max-width:1024px){
    display: ${p => p.$open ? 'block' : 'none'};
    position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 49;
  }
`;
const LogoWrap = styled.div` padding: 20px; border-bottom: 1px solid rgba(255,255,255,.08); `;
const NavWrap   = styled.nav` flex: 1; padding: 12px 0; overflow-y: auto; `;
const Divider   = styled.div` font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,.25); padding: 14px 20px 4px; `;
const NavItem   = styled(Link)`
  display: flex; align-items: center; gap: 12px; padding: 11px 20px;
  font-size: .88rem; font-weight: 500; color: rgba(255,255,255,.65);
  transition: var(--transition); border-left: 3px solid transparent;
  border-left-color: ${p => p.$active ? 'var(--primary-light)' : 'transparent'};
  background: ${p => p.$active ? 'rgba(82,183,136,.15)' : 'transparent'};
  color: ${p => p.$active ? 'var(--primary-light)' : 'rgba(255,255,255,.65)'};
  &:hover { background: rgba(255,255,255,.06); color: #fff; }
`;
const UserBar   = styled.div` padding: 14px 20px; border-top: 1px solid rgba(255,255,255,.08); display: flex; align-items: center; gap: 10px; `;
const Avatar    = styled.div` width: 36px; height: 36px; border-radius: 50%; background: var(--primary); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: .9rem; color: #fff; flex-shrink: 0; `;
const Main      = styled.div` margin-left: 240px; flex: 1; @media(max-width:1024px){margin-left:0;} `;
const Topbar    = styled.div`
  background: #fff; border-bottom: 1px solid var(--gray-100); padding: 0 28px;
  height: 64px; display: flex; align-items: center; justify-content: space-between;
  position: sticky; top: 0; z-index: 40; box-shadow: var(--shadow-sm);
`;
const Content   = styled.div` padding: 28px; @media(max-width:768px){padding:16px;} `;

export default function PanelLayout({ children, navItems, role = 'Admin' }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <Wrap>
      <SidebarOverlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <Sidebar $open={sidebarOpen}>
        <LogoWrap>
          <div style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', fontWeight:700, color:'var(--primary-light)' }}>Ameriavis</div>
          <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,.4)', marginTop:2 }}>{role} Panel</div>
        </LogoWrap>

        <NavWrap>
          {navItems.map(item => {
            if (item.divider) return <Divider key={item.label}>{item.label}</Divider>;
            const isActive = location.pathname === item.to || (item.to !== `/${role.toLowerCase()}` && location.pathname.startsWith(item.to));
            return (
              <NavItem key={item.to} to={item.to} $active={isActive} onClick={() => setSidebarOpen(false)}>
                <span style={{ fontSize:'1rem' }}>{item.icon}</span>
                {item.label}
              </NavItem>
            );
          })}
        </NavWrap>

        <UserBar>
          <Avatar>{user?.name?.[0]?.toUpperCase()}</Avatar>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'0.88rem', fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,.4)' }}>{role}</div>
          </div>
          <button onClick={handleLogout} style={{ background:'none', border:'none', color:'rgba(255,255,255,.4)', cursor:'pointer', fontSize:'1rem' }} title="Logout">🚪</button>
        </UserBar>
      </Sidebar>

      <Main>
        <Topbar>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <button onClick={() => setSidebarOpen(p => !p)} style={{ display:'none', background:'none', border:'none', cursor:'pointer', fontSize:'1.3rem', color:'var(--gray-600)', '@media(max-width:1024px)':{display:'block'} }}
              className="mobile-menu-btn">☰</button>
            <span style={{ fontSize:'0.9rem', color:'var(--gray-500)' }}>
              {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
            </span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Link to="/" style={{ fontSize:'0.85rem', color:'var(--primary)', fontWeight:500 }}>← View Store</Link>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700 }}>{user?.name?.[0]?.toUpperCase()}</div>
          </div>
        </Topbar>
        <Content>{children}</Content>
      </Main>

      <style>{`@media(max-width:1024px){.mobile-menu-btn{display:block!important;}}`}</style>
    </Wrap>
  );
}
